import { withReturn, hereNow } from './gate.jsx';
import React, { useEffect, useRef, useState } from 'react';
import { go } from './nav.js';
import { call } from './api.js';
import { RAILS, railQuery, countLabel } from './rails.js';
import { VenueCard, TabBar, Pager, onNowFirst } from './ui.jsx';
import * as rotate from './rotate.js';
/* ⚠️ WHICH MARKET — READ OFF THE ACTIVE CENTRE, NEVER TYPED IN. */
import { useCentre, marketFor, browseMarket } from './place.js';

/* ══════════════════════════════════════════════════════════════════════════
 * VIEW ALL — one rail, every venue in it, paged
 *
 * Reached from Home: each rail's "View All (n)" and its end card both land
 * here. `?all=happy_hour` and so on.
 *
 * ⚠️ THERE ARE FIVE LOCKED FILES AND THIS IS ONE COMPONENT. view_all_happy_
 * hour · view_all_daily_deals · view_all_lms · view_all_gameday, plus
 * view_all_restaurants. They differ by a heading and a deal type and nothing
 * else — so five components would be five places for the count line, the
 * pager and the empty state to drift apart. Al has already paid for that
 * shape five times this week.
 *
 * ⚠️ AND THE FIFTH IS NOT A DEAL. `view_all_restaurants` lists the market,
 * unfiltered — no deal type at all. It is the same page with the filter left
 * off, not a different screen, and treating it as one is what would have made
 * somebody write a fifth copy.
 *
 * ── THE COUNT IS THE MARKET'S ───────────────────────────────────────────────
 *
 * Al, 22 September: *"there's gonna be more than fifty… hundreds and hundreds
 * and hundreds of restaurants. If not, thousands."*  The heading says how many
 * there are; the page shows twenty at a time; the pager walks the rest. The
 * live check reads 71 venues with a Daily Deal today.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️⚠️ `const MARKET = 'hong-kong'` WAS HERE, so every View All listed Hong
   Kong however far somebody had travelled — and the heading above it counted
   Hong Kong's venues while the pin named Calgary. */
const PAGE = 20;

/* ⚠️ THE FIFTH ENTRY IS THE LOCKED SCREEN'S OWN HEADING, and its file is
   view_all_restaurants.html. The other four take their titles from RAILS, so
   a heading changed on Home changes here too and cannot drift. */
const ALL_RESTAURANTS = { type: 'restaurants', title: 'Restaurants', tab: null };

export function viewFor(key) {
  if (key === 'restaurants') return ALL_RESTAURANTS;
  return RAILS.find((r) => r.type === key) || null;
}

export default function ViewAll({ which }) {
  const view = viewFor(which);
  const centre = useCentre();
  /* outside Eleat's cities nothing is picked for them (Al-Noor, 16 Oct 2026):
     null → "Eleat isn't in your location yet. Choose a city", as Home does */
  /* never blank outside Eleat's cities — the nearest city, as Home (Al-Noor, 30 Oct 2026) */
  const market = browseMarket(centre).market;
  /* ⚠️ NEAREST FIRST, AS HOME AND EXPLORE (Al, 6 Oct 2026: "same goes for all
     the deals and view all and explore… NOT alphabetical"). No point → no
     order but A–Z, which is what this page had every time. */
  const near = (centre && Number.isFinite(centre.lat)) ? { lat: centre.lat, lng: centre.lng } : null;
  const nearKey = near ? `${near.lat.toFixed(3)},${near.lng.toFixed(3)}` : '';
  /* the page is kept in the address, as Explore's is — back from a venue
     landed on page 1 (audit, 7 Oct 2026) */
  const [page, setPage] = useState(() => {
    try { return Math.max(0, Number(new URLSearchParams(location.search).get('page')) || 0); } catch { return 0; }
  });
  useEffect(() => {
    try {
      const u = new URLSearchParams(location.search);
      if (page) u.set('page', String(page)); else u.delete('page');
      history.replaceState(history.state, '', `?${u.toString()}`);
    } catch { /* the address stays as it is */ }
  }, [page]);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!view) return;
    /* nothing to list where Eleat has no venues — said plainly below */
    if (!market) { setRes(null); setErr(null); return; }
    const mine = ++seq.current;
    /* ⚠️ THE SAME QUERY BUILDER HOME USES. Writing the URL out again here is
       how one screen ends up asking for a different thing than the count
       beside it was measuring. `restaurants` passes no deal type, which is
       the whole difference between this page and the other four. */
    const url = view.type === 'restaurants'
      ? `/discovery/search?market=${market}&limit=${PAGE}&offset=${page * PAGE}`
        + (near ? `&lat=${near.lat.toFixed(3)}&lng=${near.lng.toFixed(3)}` : '')
      : railQuery(market, view.type, { limit: PAGE, offset: page * PAGE, near });
    call(url)
      .then((d) => { if (mine === seq.current) { setRes(d); setErr(null); } })
      .catch((e) => { if (mine === seq.current) { setErr(e.message); setRes(null); } });
  }, [which, page, market, nearKey]);

  /* ⚠️ A LINK TO A LIST THAT DOES NOT EXIST GOES HOME, rather than rendering a
     page with no heading and no rows. `?all=brunch` can be typed. */
  /* deferred a tick: switching screen while this one is still drawing is a
     state change during render, which React refuses */
  if (!view) { setTimeout(() => go('?', { replace: true }), 0); return null; }

  const rows = (res && res.results) || [];
  const total = res && typeof res.total === 'number' ? res.total : null;
  const pages = total != null ? Math.ceil(total / PAGE) : 0;

  return (
    <div className="home">
      <div className="top">
        {/* ⚠️ BACK GOES TO HOME, NOT history.back(). Arriving here from a
            shared link has no history to go back to, and a dead back button
            in the corner of a page is the control this project keeps
            clearing out. */}
        <a className="back" href="?" aria-label="Home">‹</a>
        <h1 className="heroline">{view.title}</h1>
      </div>

      <main>
        {err && <p className="err">{err}</p>}
        {/* ⚠️ THE LOCATION SCREEN'S OWN WORDS — RULE 4. */}
        {market === null && (
          <p className="mut">
            {"Eleat isn't in your location yet."}{' '}
            <a className="link" href={withReturn('?location=1', hereNow())}>Choose a city</a>
          </p>
        )}
        {market !== null && !res && !err && <p className="mut">Loading…</p>}

        {res && (
          <>
            {/* ⚠️ EACH SCREEN'S OWN SENTENCE, not one generic line. Al: *"you've
                got places, but oh, 71 places."*  The locked files say what the
                71 ARE — "61 daily deals near you", not "71 places". The words
                live in `rails.js` beside the rail they belong to. */}
            <div className="cntline">
              {/* ⚠️ THE NUMBER, NOT THE FORMATTED STRING — see rails.js. */}
              {view.count
                ? view.count(total ?? rows.length)
                : <>
                    <b>{countLabel(total ?? rows.length)}</b>
                    {` place${(total ?? rows.length) === 1 ? '' : 's'} near you`}
                  </>}
            </div>

            {rows.length === 0 && (
              <p className="mut">Nothing here at the moment.</p>
            )}

            {/* ⚠️ HELD ON THE FOUR-HOUR CLOCK, NOT ADVANCED EVERY LOAD.
                Al: *"It's only explore that has a four hour turnaround"* — and
                the five View Alls are the same kind of screen. Somebody who
                finds a place here at lunchtime has to be able to find it again
                at six, so the order holds until 08:00 · 12:00 · 16:00 · 20:00.
                ⚠️ AND IT IS PER PAGE, so paging does not re-rotate underneath
                the Eleater. */}
            <div className="grid">
              {onNowFirst(rotate.held(`viewall-${view.type}-${page}`, rows), view.type)
                .map((v) => <VenueCard key={v.id} v={v} tab={view.tab} railType={view.type} />)}
            </div>

            {pages > 1 && <Pager page={page} pages={pages} onGo={setPage} />}
          </>
        )}
      </main>

      <TabBar active="home" />
    </div>
  );
}
